import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import { Quote, ArrowLeft, Loader2, Upload, X, Star } from "lucide-react";
import { T, BRAND } from "../data/content";
import { useLang } from "../context/LangContext";
import { useSiteData, API, resolveUrl } from "../context/SiteDataContext";import { Stars } from "../components/Stars";

function ShopNav() {
  const { lang, toggle, tr } = useLang();
  return (
    <header className="sticky top-0 z-40 border-b border-[#2E2418] bg-[#0C0806]/85 backdrop-blur-xl">
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 lg:px-10">
        <a href="/" className="flex items-center gap-3">
          <img src={BRAND.logo} alt="Slaly" className="h-11 w-auto rounded-md bg-[#F5ECDA] p-1 object-contain" />
        </a>
        <div className="flex items-center gap-3">
          <a href="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#B7A78C] transition-colors hover:text-[#F5ECDA]" data-testid="reviews-back">
            <ArrowLeft className="h-4 w-4" /> {tr(T.reviews.back)}
          </a>
          <button onClick={toggle} className="text-xs tracking-[0.15em] text-[#B7A78C] hover:text-[#D4AF37]">
            <span className={lang === "en" ? "text-[#D4AF37]" : ""}>EN</span>
            <span className="mx-1 opacity-40">/</span>
            <span className={lang === "fr" ? "text-[#D4AF37]" : ""}>FR</span>
          </button>
        </div>
      </div>
    </header>
  );
}

function ReviewForm({ onDone }) {
  const { tr } = useLang();
  const [name, setName] = useState("");
  const [rating, setRating] = useState(5);
  const [message, setMessage] = useState("");
  const [image, setImage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputCls = "w-full rounded-md border border-[#2E2418] bg-[#0C0806] px-3 py-2.5 text-sm text-[#F5ECDA] outline-none focus:border-[#D4AF37] transition-colors";

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/reviews/upload`, fd);
      setImage(data.url);
      toast.success("Photo ajoutée");
    } catch (err) { toast.error("Échec du téléversement de la photo"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !message.trim()) { toast.error("Merci de remplir votre nom et votre avis."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/reviews`, { name: name.trim(), rating, message: message.trim(), image });
      toast.success(tr(T.reviews.success));
      setName(""); setRating(5); setMessage(""); setImage("");
      onDone?.();
    } catch (err) { toast.error("Échec de l'envoi. Réessayez."); } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="rounded-xl border border-[#2E2418] bg-[#15100B] p-7" data-testid="review-form">
      <h2 className="font-serif text-2xl text-[#F5ECDA]">{tr(T.reviews.formTitle)}</h2>
      <p className="mb-6 mt-1 text-xs text-[#B7A78C]">{tr(T.reviews.formSub)}</p>
      <label className="mb-1 block text-xs uppercase tracking-wide text-[#B7A78C]">{tr(T.reviews.name)}</label>
      <input className={inputCls + " mb-4"} value={name} onChange={(e) => setName(e.target.value)} data-testid="review-name" />
      <label className="mb-2 block text-xs uppercase tracking-wide text-[#B7A78C]">{tr(T.reviews.rating)}</label>
      <div className="mb-4"><Stars value={rating} size={28} interactive onChange={setRating} /></div>
      <label className="mb-1 block text-xs uppercase tracking-wide text-[#B7A78C]">{tr(T.reviews.message)}</label>
      <textarea className={inputCls + " mb-4 min-h-[110px] resize-y"} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="review-message" />
      <label className="mb-2 block text-xs uppercase tracking-wide text-[#B7A78C]">{tr(T.reviews.photo)}</label>
      {image ? (
        <div className="relative mb-5 h-40 w-40 overflow-hidden rounded-md border border-[#2E2418]">
          <img src={image.startsWith("http") ? image : `${process.env.REACT_APP_BACKEND_URL}${image}`} alt="" className="h-full w-full object-cover" />
          <button type="button" onClick={() => setImage("")} className="absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded-full bg-black/70 text-red-300" data-testid="review-photo-remove"><X className="h-4 w-4" /></button>
        </div>
      ) : (
        <label className="mb-5 inline-flex cursor-pointer items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-xs uppercase tracking-[0.12em] text-[#F5ECDA] transition-colors hover:border-[#D4AF37] hover:text-[#D4AF37]" data-testid="review-photo-label">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {tr(T.reviews.photo)}
          <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="review-photo-input" />
        </label>
      )}
      <button className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-6 py-3 text-xs font-medium uppercase tracking-[0.15em] text-black transition-colors hover:bg-[#F3E5AB] disabled:opacity-50" disabled={loading} data-testid="review-submit">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : tr(T.reviews.submit)}
      </button>
    </form>
  );
}

export default function Reviews() {
  const { tr } = useLang();
  const [items, setItems] = useState(null);

  const refresh = async () => {
    try { const { data } = await axios.get(`${API}/reviews`); setItems(data); } catch (e) { setItems([]); }
  };
  useEffect(() => { window.scrollTo(0, 0); refresh(); }, []);

  return (
    <div className="min-h-screen bg-[#0C0806] text-[#F5ECDA]">
      <Toaster position="top-center" theme="dark" richColors />
      <ShopNav />
      <main className="mx-auto max-w-[1400px] px-6 py-16 lg:px-10 lg:py-24">
        <div className="mb-12 max-w-2xl">
          <div className="mb-5 flex items-center gap-3">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">{tr(T.reviews.label)}</span>
          </div>
          <h1 className="font-serif text-5xl tracking-tight md:text-6xl">{tr(T.reviews.title)}</h1>
          <p className="mt-5 text-base leading-relaxed text-[#B7A78C]">{tr(T.reviews.intro)}</p>
          <a href={BRAND.googleReview} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-xs font-medium uppercase tracking-[0.15em] text-[#F5ECDA] transition-colors hover:border-[#D4AF37] hover:text-[#D4AF37]" data-testid="reviews-google">
            <Star className="h-4 w-4 fill-[#D4AF37] text-[#D4AF37]" /> {tr(T.reviews.google)}
          </a>
        </div>

        {items && items.some((r) => r.image) && (
          <div className="mb-14" data-testid="verified-gallery">
            <div className="mb-5 flex items-center gap-3">
              <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">{tr(T.reviews.verified)}</span>
              <span className="text-xs text-[#B7A78C]">— {tr(T.reviews.verifiedSub)}</span>
            </div>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
              {items.filter((r) => r.image).map((r) => (
                <div key={`vp-${r.id}`} className="group relative aspect-square overflow-hidden rounded-md border border-[#2E2418]">
                  <img src={resolveUrl(r.image)} alt={`${r.name} result`} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            {items === null ? (
              <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#D4AF37]" /></div>
            ) : items.length === 0 ? (
              <p className="py-10 text-[#B7A78C]">{tr(T.reviews.empty)}</p>
            ) : (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2" data-testid="reviews-list">
                {items.map((r, i) => (
                  <motion.figure
                    key={r.id}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
                    className="flex flex-col rounded-lg border border-[#2E2418] bg-[#15100B] p-6"
                    data-testid={`review-item-${r.id}`}
                  >
                    <Quote className="mb-3 h-6 w-6 text-[#D4AF37]/60" />
                    <blockquote className="flex-1 text-sm leading-relaxed text-[#E9DFCB]">"{r.message}"</blockquote>
                    {r.image && <img src={resolveUrl(r.image)} alt="" className="mt-3 h-44 w-full rounded-md object-cover" data-testid={`review-photo-${r.id}`} />}
                    {r.reply && (
                      <div className="mt-3 rounded-md border-l-2 border-[#D4AF37] bg-[#0C0806] p-3">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">{tr(T.reviews.replyLabel)}</p>
                        <p className="mt-1 text-sm italic text-[#B7A78C]">{r.reply}</p>
                      </div>
                    )}
                    <figcaption className="mt-4 flex items-center justify-between border-t border-white/10 pt-3">
                      <span className="font-serif text-base text-[#F5ECDA]">{r.name}</span>
                      <Stars value={r.rating} size={14} />
                    </figcaption>
                  </motion.figure>
                ))}
              </div>
            )}
          </div>
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <ReviewForm onDone={refresh} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
